import { CaretLeftIcon, CheckIcon, HandbagIcon, HeartIcon } from '@phosphor-icons/react';

import { MEDIA } from '@/assets/media';
import type { WatchAppContent } from '@/content/types';

import styles from './WatchApp.module.css';

/**
 * Maquette d'application d'une maison horlogère : fiche produit, choix de la
 * matière (qui change seul), caractéristiques, réservation d'un essai, et la
 * notification de confirmation qui arrive d'en haut.
 */
export function WatchApp({ content }: { readonly content: WatchAppContent }) {
  return (
    <div className={styles.app}>
      <div className={styles.toast}>
        <span className={styles.toastIcon}>
          <CheckIcon weight="bold" />
        </span>
        <span className={styles.toastText}>
          <span className={styles.toastTitle}>{content.toast.title}</span>
          <span className={styles.toastDetail}>{content.toast.detail}</span>
        </span>
      </div>

      <div className={styles.hero}>
        <img
          src={MEDIA.watch.src}
          width={MEDIA.watch.width}
          height={MEDIA.watch.height}
          alt=""
          loading="lazy"
          decoding="async"
        />
        <div className={styles.topBar}>
          <span className={styles.glassButton}>
            <CaretLeftIcon weight="bold" />
          </span>
          <span className={styles.brand}>{content.brand}</span>
          <span className={styles.glassButton}>
            <HandbagIcon />
            <span className={styles.badge}>1</span>
          </span>
        </div>
        <div className={styles.heroFooter}>
          <span className={styles.galleryPill}>{content.gallery}</span>
          <span className={styles.dots}>
            <span className={styles.dotActive} />
            <span />
            <span />
          </span>
        </div>
      </div>

      <div className={styles.sheet}>
        <span className={styles.kicker}>{content.collection}</span>
        <div className={styles.titleRow}>
          <span className={styles.name}>{content.name}</span>
          <span className={styles.price}>{content.price}</span>
        </div>

        <div className={styles.materials}>
          <span className={styles.swatches}>
            <span className={`${styles.swatch} ${styles.steel}`} />
            <span className={`${styles.swatch} ${styles.roseGold}`} />
            <span className={`${styles.swatch} ${styles.titanium}`} />
            <span className={styles.ring} />
          </span>
          <span className={styles.materialNames}>
            {content.materials.map((material) => (
              <span key={material} className={styles.materialName}>
                {material}
              </span>
            ))}
          </span>
        </div>

        <div className={styles.specs}>
          {content.specs.map((spec) => (
            <span key={spec.label} className={styles.spec}>
              <span className={styles.specValue}>{spec.value}</span>
              <span className={styles.specLabel}>{spec.label}</span>
            </span>
          ))}
        </div>

        <div className={styles.booking}>
          <span className={styles.bookingTitle}>{content.booking.title}</span>
          <span className={styles.days}>
            {content.booking.days.map((day, index) => (
              <span
                key={day.date}
                className={
                  index === content.booking.chosenDay
                    ? `${styles.day} ${styles.chosen}`
                    : styles.day
                }
              >
                <span className={styles.weekday}>{day.weekday}</span>
                <span className={styles.date}>{day.date}</span>
              </span>
            ))}
          </span>
          <span className={styles.slots}>
            {content.booking.slots.map((slot, index) => (
              <span
                key={slot}
                className={
                  index === content.booking.chosenSlot
                    ? `${styles.slot} ${styles.chosen}`
                    : styles.slot
                }
              >
                {slot}
              </span>
            ))}
          </span>
        </div>

        <div className={styles.actions}>
          <span className={styles.primary}>{content.action}</span>
          <span className={styles.favorite}>
            <HeartIcon />
          </span>
        </div>
      </div>
    </div>
  );
}
